import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRightToInformationWizardContext } from '../../../hooks/useCaseRequestWizard';
import type { RightToInformationWizardDraft } from '../../../types/rightToInformationCaseRequest';
import { contactNumberError } from '../../../utils/caseFieldValidation';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { SectionCard } from '../WizardComponents';
import { RightToInformationWizardActions } from './RightToInformationWizardActions';
import {
  rightToInformationWizardPath,
  RightToInformationWizardShell,
  FieldError,
  inpClass } from './RightToInformationWizardShell';
import { validateRightToInformationStep } from './rightToInformationStepValidation';
import { useRightToInformationLookups } from './useRightToInformationLookups';
import { preferredAccessLabels, translatePreferredAccessName } from './rightToInformationHelpers';
import { useWizardFieldLabels } from '../useWizardFieldLabels';
import { useCaseCategorization } from './useCaseCategorization';

const MAX_NAME = 2000;
const MAX_SHORT = 255;
const MAX_CONTACT = 10;

export default function RightToInformationStep1CaseOverviewPage() {
  const { t } = useTranslation();
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useRightToInformationWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const { preferredMethodOfAccess, preferredLanguages } = useRightToInformationLookups('step1');
  const cat = useCaseCategorization(draft.fileCategoryId, draft.subFileCategoryId);
  const justificationRequired = draft.involvesLifeOrPersonalLiberty === 'yes';

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  useEffect(() => {
    if (draft.isSriLankanCitizen === 'yes' || draft.isSriLankanCitizen === 'no') return;
    setDraft((d: RightToInformationWizardDraft) => ({ ...d, isSriLankanCitizen: 'no' }));
  }, [draft.isSriLankanCitizen, setDraft]);

  useEffect(() => {
    if (draft.involvesLifeOrPersonalLiberty === 'yes' || draft.involvesLifeOrPersonalLiberty === 'no') return;
    setDraft((d: RightToInformationWizardDraft) => ({ ...d, involvesLifeOrPersonalLiberty: 'no' }));
  }, [draft.involvesLifeOrPersonalLiberty, setDraft]);

  const validate = (): boolean => {
    const selectedFileCategory = cat.fileCategories.find((c) => c.id === draft.fileCategoryId);
    const { valid, errors: nextErrors, specs } = validateRightToInformationStep(1, draft, {
      subCategories: cat.subCategories,
      loadingSubCategories: cat.loadingSubCategories,
      fileCategoryCode: selectedFileCategory?.code });
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  const togglePreferredAccess = (id: string) => {
    setDraft((d) => {
      const has = d.preferredMethodOfAccessIds.includes(id);
      const ids = has ? d.preferredMethodOfAccessIds.filter((x) => x !== id) : [...d.preferredMethodOfAccessIds, id];
      const names = preferredAccessLabels(ids, preferredMethodOfAccess);
      return { ...d, preferredMethodOfAccessIds: ids, preferredMethodOfAccessNames: names };
    });
    clearErr('preferredMethodOfAccessIds');
  };

  const next = () => {
    if (!validate()) return;
    nav(rightToInformationWizardPath(2, draft.draftRequestId));
  };

  return (
    <RightToInformationWizardShell
      currentStep={1}
      draft={draft}
      setDraft={setDraft}
      categorizationEditable
      categorizationErrors={errors}
      onClearCategorizationError={clearErr}
    >
      <SectionCard title={f.sectionRequesterDetails}>
        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.requestorName} <span className="req">*</span>
            </label>
            <input
              id="rti-requestorName"
              className={inpClass(Boolean(errors.requestorName))}
              type="text"
              value={draft.requestorName}
              onChange={(e) => {
                setDraft((d) => ({ ...d, requestorName: maxLen('requestorName', e.target.value, MAX_NAME) }));
              }}
              placeholder={f.enterRequestorName}
            />
            <FieldError message={errors.requestorName} />
          </div>
          <div className="ccr-fg">
            <label>
              {f.nicPassportNumber} <span className="req">*</span>
            </label>
            <input
              id="rti-nicOrPassport"
              className={inpClass(Boolean(errors.nicOrPassportNumber))}
              type="text"
              value={draft.nicOrPassportNumber}
              onChange={(e) => {
                setDraft((d) => ({ ...d, nicOrPassportNumber: maxLen('nicOrPassportNumber', e.target.value, MAX_SHORT) }));
              }}
              placeholder={f.enterNicOrPassport}
            />
            <FieldError message={errors.nicOrPassportNumber} />
          </div>
        </div>

        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>
            {f.address} <span className="req">*</span>
          </label>
          <textarea
            id="rti-address"
            className={inpClass(Boolean(errors.address))}
            rows={3}
            value={draft.address}
            onChange={(e) => {
              setDraft((d) => ({ ...d, address: maxLen('address', e.target.value, MAX_NAME) }));
            }}
            placeholder={f.enterAddress}
          />
          <FieldError message={errors.address} />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.contactNumber}</label>
            <input
              id="rti-contactNumber"
              className={inpClass(Boolean(errors.contactNumber))}
              type="text"
              inputMode="numeric"
              value={draft.contactNumber}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, MAX_CONTACT);
                setDraft((d) => ({ ...d, contactNumber: digits }));
                const err = contactNumberError(digits);
                setErrors((prev) => ({ ...prev, contactNumber: err ?? '' }));
              }}
              placeholder={f.contactNumberExample}
            />
            <FieldError message={errors.contactNumber} />
          </div>
          <div className="ccr-fg">
            <label>{f.emailAddress}</label>
            <input
              id="rti-emailAddress"
              className={inpClass(Boolean(errors.emailAddress))}
              type="email"
              value={draft.emailAddress}
              onChange={(e) => {
                setDraft((d) => ({ ...d, emailAddress: maxLen('emailAddress', e.target.value, MAX_SHORT) }));
              }}
              placeholder={f.enterEmailAddress}
            />
            <FieldError message={errors.emailAddress} />
          </div>
        </div>

        <div className="ccr-fg" id="rti-citizen" style={{ marginBottom: 0 }}>
          <label>
            {f.isSriLankanCitizen} <span className="req">*</span>
          </label>
          <div className="ccr-radio-row">
            <label className="ccr-radio">
              <input
                type="radio"
                name="rtiCitizen"
                checked={draft.isSriLankanCitizen === 'yes'}
                onChange={() => setDraft((d) => ({ ...d, isSriLankanCitizen: 'yes' }))}
              />
              <span>{f.yes}</span>
            </label>
            <label className="ccr-radio">
              <input
                type="radio"
                name="rtiCitizen"
                checked={draft.isSriLankanCitizen === 'no'}
                onChange={() => setDraft((d) => ({ ...d, isSriLankanCitizen: 'no' }))}
              />
              <span>{f.no}</span>
            </label>
          </div>
          <FieldError message={errors.isSriLankanCitizen} />
        </div>
      </SectionCard>

      <SectionCard title={f.sectionInformationRequestDetails}>
        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>
            {f.specificPublicAuthorityName} <span className="req">*</span>
          </label>
          <input
            id="rti-publicAuthority"
            className={inpClass(Boolean(errors.specificPublicAuthorityName))}
            type="text"
            value={draft.specificPublicAuthorityName}
            onChange={(e) => {
              setDraft((d) => ({ ...d, specificPublicAuthorityName: maxLen('specificPublicAuthorityName', e.target.value, MAX_SHORT) }));
            }}
            placeholder={f.enterPublicAuthorityName}
          />
          <FieldError message={errors.specificPublicAuthorityName} />
        </div>

        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>
            {f.requestedInformationDetails} <span className="req">*</span>
          </label>
          <textarea
            id="rti-requestedDetails"
            className={inpClass(Boolean(errors.requestedInformationDetails))}
            rows={4}
            value={draft.requestedInformationDetails}
            onChange={(e) => {
              setDraft((d) => ({ ...d, requestedInformationDetails: maxLen('requestedInformationDetails', e.target.value, MAX_NAME) }));
            }}
            placeholder={f.describeRequestedInformation}
          />
          <FieldError message={errors.requestedInformationDetails} />
        </div>

        <div className="ccr-fg" style={{ marginBottom: 0 }}>
          <label>{f.informationRequestedTimePeriod}</label>
          <input
            className={inpClass(Boolean(errors.informationRequestedTimePeriod))}
            type="text"
            value={draft.informationRequestedTimePeriod}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                informationRequestedTimePeriod: maxLen('informationRequestedTimePeriod', e.target.value, MAX_SHORT) }))
            }
            placeholder={f.enterTimePeriodIfApplicable}
          />
          <FieldError message={errors.informationRequestedTimePeriod} />
        </div>
      </SectionCard>

      <SectionCard title={f.sectionAdditionalInformation}>
        <div id="rti-preferredAccess" style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 10 }}>
            {f.preferredMethodOfAccess} <span className="req">*</span>
          </label>
          <div className="ccr-check-grid">
            {preferredMethodOfAccess.map((m) => (
              <label key={m.id} className="ccr-check">
                <input
                  type="checkbox"
                  checked={draft.preferredMethodOfAccessIds.includes(m.id)}
                  onChange={() => togglePreferredAccess(m.id)}
                />
                <span>{translatePreferredAccessName(m.name, t)}</span>
              </label>
            ))}
          </div>
          <FieldError message={errors.preferredMethodOfAccessIds} />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.preferredLanguageForResponse} <span className="req">*</span>
            </label>
            <WizardSelect
              id="rti-preferredLanguage"
              hasError={Boolean(errors.preferredLanguageId)}
              value={draft.preferredLanguageId}
              placeholder={f.selectPreferredLanguage}
              options={preferredLanguages.map((l) => ({ id: l.id, label: l.name }))}
              onChange={(id) => {
                const name = preferredLanguages.find((l) => l.id === id)?.name ?? '';
                setDraft((d) => ({ ...d, preferredLanguageId: id, preferredLanguageName: name }));
                clearErr('preferredLanguageId');
              }}
            />
            <FieldError message={errors.preferredLanguageId} />
          </div>
        </div>

        <div className="ccr-fg" id="rti-lifeLiberty" style={{ marginBottom: 14 }}>
          <label>
            {f.involvesLifeOrPersonalLiberty} <span className="req">*</span>
          </label>
          <div className="ccr-radio-row">
            <label className="ccr-radio">
              <input
                type="radio"
                name="rtiLifeLiberty"
                checked={draft.involvesLifeOrPersonalLiberty === 'yes'}
                onChange={() => setDraft((d) => ({ ...d, involvesLifeOrPersonalLiberty: 'yes' }))}
              />
              <span>{f.yes}</span>
            </label>
            <label className="ccr-radio">
              <input
                type="radio"
                name="rtiLifeLiberty"
                checked={draft.involvesLifeOrPersonalLiberty === 'no'}
                onChange={() => {
                  setDraft((d) => ({ ...d, involvesLifeOrPersonalLiberty: 'no' }));
                  clearErr('justification');
                }}
              />
              <span>{f.no}</span>
            </label>
          </div>
          <FieldError message={errors.involvesLifeOrPersonalLiberty} />
        </div>

        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>
            {f.justification} {justificationRequired ? <span className="req">*</span> : null}
          </label>
          <textarea
            id="rti-justification"
            className={inpClass(Boolean(errors.justification))}
            rows={3}
            value={draft.justification}
            onChange={(e) => {
              setDraft((d) => ({ ...d, justification: maxLen('justification', e.target.value, MAX_NAME) }));
            }}
            placeholder={f.provideJustification}
          />
          <FieldError message={errors.justification} />
        </div>

        <div className="ccr-fg" style={{ marginBottom: 0 }}>
          <label>{f.additionalNotes}</label>
          <textarea
            className={inpClass(Boolean(errors.additionalNotes))}
            rows={3}
            value={draft.additionalNotes}
            onChange={(e) => setDraft((d) => ({ ...d, additionalNotes: maxLen('additionalNotes', e.target.value, MAX_NAME) }))}
            placeholder={f.additionalNotesOptional}
          />
          <FieldError message={errors.additionalNotes} />
        </div>
      </SectionCard>

      <RightToInformationWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={1}
        onNext={next}
        validateBeforeSave={validate}
      />
    </RightToInformationWizardShell>
  );
}
